import { getClassDisplayName } from "../../utils/classPresentation";
import React, {
  startTransition,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiImage,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiUploadCloud,
} from "react-icons/fi";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import MenuLateral from "../../Components/MenuLateral";
import CadastroPageHeader from "../../Components/CadastroPageHeader";
import FieldLabel from "../../Components/FieldLabel";
import { LoginContext } from "../../context/LoginContext";
import api from "../../services/api";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import { subscribeToUploadBatchEvents } from "../../services/uploadBatchEvents";
import { USER_ROLES } from "../../auth/session";
import "./index.css";

const DEFAULT_UPLOAD_CAPACITY = {
  maxFilesPerRequest: 20,
  maxFilesPerManifest: 1000,
  maxUploadSizeBytes: 150 * 1024 * 1024,
  supportedMimeTypes: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/tiff",
    "image/tif",
    "image/webp",
  ],
};
const MIME_EXTENSIONS = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/jpg": [".jpg"],
  "image/png": [".png"],
  "image/tiff": [".tif", ".tiff"],
  "image/tif": [".tif"],
  "image/webp": [".webp"],
};
const GENERIC_FILE_MIME_TYPES = new Set(["", "application/octet-stream"]);
const MIME_LABELS = {
  "image/jpeg": "JPEG",
  "image/jpg": "JPG",
  "image/png": "PNG",
  "image/tiff": "TIFF",
  "image/tif": "TIF",
  "image/webp": "WebP",
};
const RESUMABLE_BATCH_STORAGE_KEY = "formari.upload.active-batch.v1";
const TERMINAL_BATCH_STATUSES = new Set(["completed", "partial_failed", "failed"]);
const EVENT_PREVIEW_PHOTO_LIMIT = 5;

const normalizeMimeType = (mimeType) =>
  typeof mimeType === "string" ? mimeType.trim().toLowerCase() : "";

const inferMimeTypeFromExtension = (file, allowedMimeTypes) => {
  const extension = `.${file.name?.split(".").pop()?.toLowerCase() || ""}`;
  return allowedMimeTypes.find((mimeType) =>
    MIME_EXTENSIONS[mimeType]?.includes(extension),
  ) || "";
};

const prepareSelectedFile = (file, allowedMimeTypes) => {
  const declaredMimeType = normalizeMimeType(file.type);
  if (allowedMimeTypes.includes(declaredMimeType)) return file;
  if (!GENERIC_FILE_MIME_TYPES.has(declaredMimeType)) return null;

  const inferredMimeType = inferMimeTypeFromExtension(file, allowedMimeTypes);
  if (!inferredMimeType || typeof File === "undefined") return null;

  return new File([file], file.name, {
    type: inferredMimeType,
    lastModified: file.lastModified,
  });
};

const ITEM_STATUS_LABELS = {
  ready: "Pronto para upload",
  sending: "Enviando bytes",
  not_sent: "Aguardando retomada",
  needs_file: "Arquivo local indisponivel",
  processing: "Recebido; processamento tecnico em andamento",
  accepted: "Aceito no lote",
  failed: "Falhou",
  reconcile_required: "Aguardando reconciliacao segura",
};

const createClientItemId = () => {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  return `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isBatchTerminal = (batch) =>
  Boolean(batch?.closedAt) || TERMINAL_BATCH_STATUSES.has(batch?.status);

const isUnsentBatchWithoutFiles = (batch) =>
  batch?.status === "pending" &&
  !batch.closedAt &&
  Array.isArray(batch.items) &&
  batch.items.length > 0 &&
  batch.items.every(
    (item) => item.status === "not_sent" && !item.photoId,
  );

const mapSnapshotStatus = (item, existingItem) => {
  if (item.status === "accepted") return "accepted";
  if (item.status === "failed") return "failed";
  if (item.status === "uploading") return "processing";

  return existingItem?.file ? "ready" : "needs_file";
};

const mergeSnapshotItems = (snapshot, currentItems) => {
  if (!snapshot?.items?.length) return currentItems;

  const currentById = new Map(
    currentItems.map((item) => [item.clientItemId, item]),
  );

  return snapshot.items.map((item) => {
    const existingItem = currentById.get(item.clientItemId);

    return {
      clientItemId: item.clientItemId,
      name: item.name,
      sizeBytes: Number(item.sizeBytes || 0),
      file: existingItem?.file || null,
      status: mapSnapshotStatus(item, existingItem),
      remoteStatus: item.status,
      transportPercent: item.status === "not_sent" ? 0 : 100,
      error: item.error || null,
      imageProcessing: item.imageProcessing || existingItem?.imageProcessing || {
        status: item.status === "failed" ? "failed" : "not_started",
      },
      recognition: item.recognition || existingItem?.recognition || {
        status: "not_started",
      },
    };
  });
};

function Upload() {
  const { LoggedUser } = useContext(LoginContext);
  const role = LoggedUser?.role;
  const canUseUpload = role === USER_ROLES.ADMIN || role === USER_ROLES.PHOTOGRAPHER;
  const canOpenPublication = role === USER_ROLES.ADMIN;

  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [buscaEvento, setBuscaEvento] = useState("");
  const [queueItems, setQueueItems] = useState([]);
  const [fileSearchInput, setFileSearchInput] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const queueScrollRef = useRef(null);
  const visibleQueueItems = useMemo(() => {
    const term = fileSearch.toLowerCase();
    return queueItems.filter((item) => item.name.toLowerCase().includes(term));
  }, [queueItems, fileSearch]);

  useEffect(() => {
    if (queueScrollRef.current) queueScrollRef.current.scrollTop = 0;
  }, [fileSearch]);

  useEffect(() => {
    if (!queueItems.length) {
      setFileSearchInput("");
      setFileSearch("");
    }
  }, [queueItems.length]);
  const [uploading, setUploading] = useState(false);
  const [restoringBatch, setRestoringBatch] = useState(false);
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchMonitoringPaused, setBatchMonitoringPaused] = useState(false);
  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    uploaded: 0,
    failed: 0,
  });
  const [resumoEvento, setResumoEvento] = useState({
    totalPhotos: 0,
    photos: [],
    loading: false,
  });
  const [uploadBatches, setUploadBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [streamStatus, setStreamStatus] = useState("");
  const [uploadCapacity, setUploadCapacity] = useState(DEFAULT_UPLOAD_CAPACITY);
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  const [capacityError, setCapacityError] = useState("");

  const restorationRequestedRef = useRef(false);
  const queueItemsRef = useRef([]);
  const discardedBatchIdsRef = useRef(new Set());
  const buscaAdiada = useDeferredValue(buscaEvento);
  const queueLocked = uploading || restoringBatch || Boolean(activeBatch);
  const canAttachMissingFiles =
    !uploading &&
    Boolean(activeBatch) &&
    queueItems.some((item) => item.status === "needs_file");
  const failedItems = queueItems.filter((item) => item.status === "failed");
  const maxFileSizeBytes = Number(
    uploadCapacity.maxUploadSizeBytes || DEFAULT_UPLOAD_CAPACITY.maxUploadSizeBytes,
  );
  const maxFileSizeMb = Math.round(maxFileSizeBytes / 1024 / 1024);
  const allowedMimeTypes = uploadCapacity.supportedMimeTypes?.length
    ? uploadCapacity.supportedMimeTypes.map(normalizeMimeType).filter(Boolean)
    : DEFAULT_UPLOAD_CAPACITY.supportedMimeTypes;
  const acceptedExtensions = Array.from(
    new Set(allowedMimeTypes.flatMap((type) => MIME_EXTENSIONS[type] || [])),
  );
  const acceptedFileTypes = [...acceptedExtensions, ...allowedMimeTypes].join(",");

  const mensagemDoErro = (error) => {
    if (!error) return "Erro desconhecido";

    const resp = error.response;
    const data = resp?.data;

    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.message)) return data.message.join(", ");
    if (typeof data === "string") return data;

    return error.message || "Erro ao comunicar com a API";
  };

  const formatarTurmas = (classes = []) => {
    if (!classes.length) return "Sem turmas vinculadas";
    return classes.map((item) => getClassDisplayName(item)).join(", ");
  };

  const formatarInstituicoes = (classes = []) => {
    const instituicoes = Array.from(
      new Set(
        classes
          .map((item) => item.institution?.name)
          .filter(Boolean),
      ),
    );

    if (!instituicoes.length) return "Instituicao nao informada";
    return instituicoes.join(", ");
  };

  const setProgressFromItems = (items) => {
    const accepted = items.filter((item) => item.status === "accepted").length;
    const failed = items.filter((item) => item.status === "failed").length;

    setProgress({
      total: items.length,
      processed: accepted + failed,
      uploaded: accepted,
      failed,
    });
  };

  const persistActiveBatch = (batch, paused = false) => {
    if (!batch?.id || !batch?.eventId || isBatchTerminal(batch)) {
      window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(
      RESUMABLE_BATCH_STORAGE_KEY,
      JSON.stringify({ batchId: batch.id, eventId: batch.eventId, paused }),
    );
  };

  const applyBatchSnapshot = (batch, sourceItems, options = {}) => {
    const mergedItems = mergeSnapshotItems(batch, sourceItems);
    const closed = isBatchTerminal(batch);
    const paused = options.paused ?? batchMonitoringPaused;

    queueItemsRef.current = mergedItems;
    setQueueItems(mergedItems);
    setProgress({
      total: Number(batch?.totalFiles ?? mergedItems.length),
      processed: Number(
        batch?.processedFiles ??
        mergedItems.filter((item) => ["accepted", "failed"].includes(item.status)).length,
      ),
      uploaded: Number(
        batch?.uploadedFiles ??
        mergedItems.filter((item) => item.status === "accepted").length,
      ),
      failed: Number(
        batch?.failedFiles ??
        mergedItems.filter((item) => item.status === "failed").length,
      ),
    });

    if (closed) {
      setActiveBatch(null);
      setBatchMonitoringPaused(false);
      window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
    } else {
      setActiveBatch({
        id: batch.id,
        eventId: batch.eventId,
        status: batch.status,
      });
      persistActiveBatch(batch, paused);
    }

    return mergedItems;
  };

  const carregarEventos = async () => {
    if (!canUseUpload) return;

    setLoadingEventos(true);
    try {
      const resp = await api.get("/events", {
        params: { limit: 100 },
      });

      const listaEventos = resp.data?.data || resp.data?.events || [];

      startTransition(() => {
        setEventos(listaEventos);
      });
    } catch (error) {
      Swal.fire("Erro", mensagemDoErro(error), "error");
    } finally {
      setLoadingEventos(false);
    }
  };

  const carregarCapacidade = async () => {
    if (!canUseUpload) return;

    setLoadingCapacity(true);
    setCapacityError("");
    try {
      const resp = await api.get("/upload/event-photos/capacity");
      const capacity = resp.data || {};
      const quality = capacity.quality || {};
      setUploadCapacity({
        maxFilesPerRequest: Number(
          capacity.maxFilesPerRequest || DEFAULT_UPLOAD_CAPACITY.maxFilesPerRequest,
        ),
        maxFilesPerManifest: Number(
          capacity.maxFilesPerManifest || DEFAULT_UPLOAD_CAPACITY.maxFilesPerManifest,
        ),
        maxUploadSizeBytes: Number(
          capacity.maxUploadSizeBytes || DEFAULT_UPLOAD_CAPACITY.maxUploadSizeBytes,
        ),
        supportedMimeTypes: quality.supportedMimeTypes?.length
          ? quality.supportedMimeTypes
          : DEFAULT_UPLOAD_CAPACITY.supportedMimeTypes,
      });
    } catch (error) {
      setCapacityError("Nao foi possivel carregar os limites do servidor; os limites seguros locais foram mantidos.");
    } finally {
      setLoadingCapacity(false);
    }
  };

  const carregarResumoEvento = async (eventId) => {
    if (!eventId) {
      setResumoEvento({ totalPhotos: 0, photos: [], loading: false });
      return;
    }

    setResumoEvento((current) => ({ ...current, loading: true }));

    try {
      const resp = await api.get(
        `/upload/event-photos/${eventId}?page=1&limit=${EVENT_PREVIEW_PHOTO_LIMIT}`,
      );
      setResumoEvento({
        totalPhotos: resp.data?.pagination?.totalPhotos || 0,
        photos: resp.data?.photos || [],
        loading: false,
      });
    } catch (error) {
      setResumoEvento({
        totalPhotos: 0,
        photos: [],
        loading: false,
      });
      Swal.fire("Erro", mensagemDoErro(error), "error");
    }
  };

  const carregarLotesEvento = async (eventId) => {
    if (!eventId) {
      setUploadBatches([]);
      return;
    }

    setLoadingBatches(true);
    try {
      const resp = await api.get(`/upload/event-photos/${eventId}/batches`);
      const batches = resp.data?.batches || [];
      setUploadBatches(
        batches.filter((batch) => !discardedBatchIdsRef.current.has(batch.id)),
      );
    } catch (error) {
      setUploadBatches([]);
      Swal.fire("Erro", mensagemDoErro(error), "error");
    } finally {
      setLoadingBatches(false);
    }
  };

  const carregarSnapshotDoLote = async (batchId, sourceItems = queueItems, options = {}) => {
    try {
      const resp = await api.get(`/upload/event-photos/batches/${batchId}`);
      const batch = resp.data?.batch;

      if (!batch?.id) {
        throw new Error("Snapshot do lote nao foi retornado");
      }

      const items = applyBatchSnapshot(batch, sourceItems, {
        paused: options.paused,
      });
      return { batch, items };
    } catch (error) {
      if (options.showError) {
        Swal.fire("Nao foi possivel atualizar o lote", mensagemDoErro(error), "error");
      }
      return null;
    }
  };

  useEffect(() => {
    carregarEventos();
    carregarCapacidade();
    // A permissao de upload e o gatilho completo desta carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseUpload]);

  useEffect(() => {
    carregarResumoEvento(eventoSelecionado?.id);
    carregarLotesEvento(eventoSelecionado?.id);
    // O evento selecionado e o gatilho completo destas cargas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoSelecionado?.id]);

  useEffect(() => {
    queueItemsRef.current = queueItems;
  }, [queueItems]);

  useEffect(() => {
    if (!activeBatch?.id || batchMonitoringPaused || uploading) {
      setStreamStatus(
        batchMonitoringPaused
          ? "Acompanhamento pausado. Corrija a pendencia e retome o lote manualmente."
          : "",
      );
      return undefined;
    }

    let disposed = false;
    let refreshTimer;
    let refreshing = false;
    let refreshQueued = false;

    const refreshSnapshot = async () => {
      if (disposed || refreshing) {
        refreshQueued = true;
        return;
      }

      refreshing = true;
      try {
        const resp = await api.get(`/upload/event-photos/batches/${activeBatch.id}`);
        const batch = resp.data?.batch;
        if (!batch?.id || disposed) return;

        applyBatchSnapshot(batch, queueItemsRef.current);
        setStreamStatus("Atualizado pelo estado persistido do lote.");
      } catch (error) {
        if (error.response?.status === 403) {
          setStreamStatus("Sem permissao para acompanhar este lote.");
        } else if (error.response?.status === 401) {
          setStreamStatus("Sessao indisponivel para acompanhar o lote.");
        } else {
          setStreamStatus("Reconectando acompanhamento do lote.");
        }
      } finally {
        refreshing = false;
        if (refreshQueued && !disposed) {
          refreshQueued = false;
          scheduleSnapshot();
        }
      }
    };

    const scheduleSnapshot = () => {
      if (disposed || refreshTimer) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        void refreshSnapshot();
      }, 250);
    };

    setStreamStatus("Conectando acompanhamento do lote.");
    const unsubscribe = subscribeToUploadBatchEvents({
      batchId: activeBatch.id,
      onOpen: ({ reconnected }) => {
        if (disposed) return;
        setStreamStatus(
          reconnected ? "Reconectado; conferindo estado persistido." : "Acompanhamento conectado.",
        );
        scheduleSnapshot();
      },
      onEvent: (event) => {
        if (disposed || event.type === "heartbeat") return;
        scheduleSnapshot();
      },
      onReconnect: () => {
        if (disposed) return;
        setStreamStatus("Reconectando acompanhamento do lote.");
        scheduleSnapshot();
      },
      onAuthorizationError: ({ status }) => {
        if (disposed) return;
        setStreamStatus(
          status === 403
            ? "Sem permissao para acompanhar este lote."
            : "Sessao indisponivel para acompanhar o lote.",
        );
      },
      onError: () => {
        if (!disposed) setStreamStatus("Conexao interrompida; tentando reconectar.");
      },
    });

    return () => {
      disposed = true;
      window.clearTimeout(refreshTimer);
      unsubscribe();
    };
    // A conexao depende do lote ativo fora do transporte; callbacks locais usam refs/snapshot atual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBatch?.id, batchMonitoringPaused, uploading]);

  useEffect(() => {
    if (restorationRequestedRef.current || !eventos.length) return;

    const storedValue = window.sessionStorage.getItem(RESUMABLE_BATCH_STORAGE_KEY);
    if (!storedValue) {
      restorationRequestedRef.current = true;
      return;
    }

    let storedBatch;
    try {
      storedBatch = JSON.parse(storedValue);
    } catch {
      window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
      restorationRequestedRef.current = true;
      return;
    }

    if (!storedBatch?.batchId || !storedBatch?.eventId) {
      window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
      restorationRequestedRef.current = true;
      return;
    }

    restorationRequestedRef.current = true;
    let active = true;
    const paused = Boolean(storedBatch.paused);
    setRestoringBatch(true);
    setBatchMonitoringPaused(paused);

    const eventoDoLote = eventos.find((evento) => evento.id === storedBatch.eventId);
    if (eventoDoLote) {
      setEventoSelecionado(eventoDoLote);
    }

    api
      .get(`/upload/event-photos/batches/${storedBatch.batchId}`)
      .then(async (resp) => {
        if (!active || !resp.data?.batch) return;
        const batch = resp.data.batch;

        if (isUnsentBatchWithoutFiles(batch)) {
          const clearLocalBatch = () => {
            window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
            if (!active) return;

            discardedBatchIdsRef.current.add(batch.id);
            queueItemsRef.current = [];
            setQueueItems([]);
            setProgress({ total: 0, processed: 0, uploaded: 0, failed: 0 });
            setActiveBatch(null);
            setBatchMonitoringPaused(false);
            setStreamStatus("");
            setUltimoResultado(null);
            setUploadBatches((currentBatches) =>
              currentBatches.filter((currentBatch) => currentBatch.id !== batch.id),
            );
          };

          try {
            await api.delete(`/upload/event-photos/batches/${batch.id}`);
            clearLocalBatch();
            return;
          } catch (error) {
            if (error.response?.status === 404) {
              clearLocalBatch();
              return;
            }

            if (!active) return;
            applyBatchSnapshot(batch, [], { paused });
            Swal.fire(
              "Lote mantido",
              "Nao foi possivel remover automaticamente o lote sem arquivos. Reanexe os arquivos pendentes para continuar.",
              "warning",
            );
            return;
          }
        }

        applyBatchSnapshot(batch, [], { paused });
      })
      .catch((error) => {
        if (!active) return;
        window.sessionStorage.removeItem(RESUMABLE_BATCH_STORAGE_KEY);
        setActiveBatch(null);
        setBatchMonitoringPaused(false);
        if (error.response?.status === 404) {
          queueItemsRef.current = [];
          setQueueItems([]);
          setProgress({ total: 0, processed: 0, uploaded: 0, failed: 0 });
          return;
        }
        Swal.fire(
          "Lote nao restaurado",
          "Nao foi possivel recuperar o snapshot autorizado do lote. Nenhum arquivo sera reenviado automaticamente.",
          "warning",
        );
      })
      .finally(() => {
        if (active) setRestoringBatch(false);
      });

    return () => {
      active = false;
    };
    // A restauracao e executada somente depois da lista de eventos estar disponivel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventos]);

  const eventosFiltrados = eventos.filter((evento) => {
    const termo = buscaAdiada.trim().toLowerCase();

    if (!termo) return true;

    const texto = [
      evento.name,
      evento.location,
      formatarTurmas(evento.classes || []),
      formatarInstituicoes(evento.classes || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return texto.includes(termo);
  });

  const validarArquivos = (selectedFiles) => {
    if (selectedFiles.length > uploadCapacity.maxFilesPerManifest) {
      Swal.fire(
        "Quantidade de arquivos acima do limite",
        `Selecione no maximo ${uploadCapacity.maxFilesPerManifest} arquivos por lote.`,
        "warning",
      );
      return [];
    }

    const validos = [];
    const invalidos = [];

    selectedFiles.forEach((file) => {
      const preparedFile = prepareSelectedFile(file, allowedMimeTypes);
      const tamanhoValido = file.size <= maxFileSizeBytes;

      if (preparedFile && tamanhoValido) {
        validos.push(preparedFile);
        return;
      }

      invalidos.push(file.name);
    });

    if (invalidos.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Alguns arquivos foram ignorados",
        text: `${invalidos.length} arquivo(s) possuem formato nao suportado ou excedem ${maxFileSizeMb}MB.`,
      });
    }

    return validos;
  };

  const selecionarArquivos = (event) => {
    const selectedFiles = validarArquivos(Array.from(event.target.files || []));

    if (activeBatch) {
      const filesDisponiveis = [...selectedFiles];
      let arquivosReanexados = 0;

      const nextQueue = queueItems.map((item) => {
        if (item.status !== "needs_file") return item;

        const fileIndex = filesDisponiveis.findIndex(
          (file) => file.name === item.name && file.size === item.sizeBytes,
        );
        if (fileIndex < 0) return item;

        const [file] = filesDisponiveis.splice(fileIndex, 1);
        arquivosReanexados += 1;
        return {
          ...item,
          file,
          status: "ready",
          error: null,
        };
      });

      setQueueItems(nextQueue);
      setProgressFromItems(nextQueue);
      event.target.value = "";

      if (arquivosReanexados === 0) {
        Swal.fire(
          "Arquivo nao correspondente",
          "Durante a retomada, somente arquivos com o mesmo nome e tamanho do item pendente podem ser reanexados.",
          "warning",
        );
      }
      return;
    }

    const nextQueue = selectedFiles.map((file) => ({
      clientItemId: createClientItemId(),
      name: file.name,
      sizeBytes: file.size,
      file,
      status: "ready",
      remoteStatus: null,
      transportPercent: 0,
      error: null,
      imageProcessing: { status: "not_started" },
      recognition: { status: "not_started" },
    }));

    setQueueItems(nextQueue);
    setUltimoResultado(null);
    setProgressFromItems(nextQueue);
  };

  const removerArquivoDaFila = (clientItemId) => {
    if (queueLocked) return;

    const nextQueue = queueItems.filter((item) => item.clientItemId !== clientItemId);
    setQueueItems(nextQueue);
    setProgressFromItems(nextQueue);
  };

  const marcarGrupoComoEnviando = (items, clientItemIds, percent = 0) =>
    items.map((item) =>
      clientItemIds.includes(item.clientItemId)
        ? { ...item, status: "sending", transportPercent: percent, error: null }
        : item,
    );

  const enviarItensDoLote = async (batch, initialItems) => {
    let workingItems = initialItems;
    let interruptido = false;

    setUploading(true);
    setUltimoResultado(null);

    const pendingItems = () =>
      workingItems.filter(
        (item) =>
          item.file &&
          (item.status === "ready" || item.status === "not_sent"),
      );

    while (pendingItems().length > 0 && !interruptido) {
      const itemsDoRequest = pendingItems().slice(
        0,
        uploadCapacity.maxFilesPerRequest,
      );
      const clientItemIds = itemsDoRequest.map((item) => item.clientItemId);
      workingItems = marcarGrupoComoEnviando(workingItems, clientItemIds);
      setQueueItems(workingItems);

      const formData = new FormData();
      itemsDoRequest.forEach((item) => formData.append("images", item.file));
      formData.append("eventId", batch.eventId);
      formData.append("uploadBatchId", batch.id);
      formData.append("clientItemIds", JSON.stringify(clientItemIds));

      try {
        const resp = await api.post("/upload/event-photos", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            const percent = event.total
              ? Math.min(100, Math.round((event.loaded / event.total) * 100))
              : 0;
            setQueueItems((currentItems) =>
              marcarGrupoComoEnviando(currentItems, clientItemIds, percent),
            );
          },
        });

        if (resp.data?.batch) {
          workingItems = applyBatchSnapshot(resp.data.batch, workingItems, { paused: false });
        }
      } catch (error) {
        setBatchMonitoringPaused(true);
        const reconciled = await carregarSnapshotDoLote(batch.id, workingItems, {
          paused: true,
        });

        if (reconciled) {
          workingItems = reconciled.items;
        } else {
          workingItems = workingItems.map((item) =>
            clientItemIds.includes(item.clientItemId)
              ? {
                ...item,
                status: "reconcile_required",
                error: "Envio interrompido. Atualize o snapshot antes de tentar continuar.",
              }
              : item,
          );
          setQueueItems(workingItems);
        }

        const loteContinuaAberto = !reconciled?.batch || !isBatchTerminal(reconciled.batch);
        if (loteContinuaAberto) {
          persistActiveBatch({ ...batch, eventId: batch.eventId }, true);
          setStreamStatus("Envio pausado. Corrija a pendencia antes de retomar o lote.");
        } else {
          setBatchMonitoringPaused(false);
        }

        setUploading(false);
        await Swal.fire("Envio interrompido", mensagemDoErro(error), "warning");
        return;
      }

      await carregarLotesEvento(batch.eventId);
    }

    const snapshotFinal = await carregarSnapshotDoLote(batch.id, workingItems, {
      paused: false,
    });
    const finalItems = snapshotFinal?.items || workingItems;
    const accepted = finalItems.filter((item) => item.status === "accepted").length;
    const failed = finalItems.filter((item) => item.status === "failed").length;

    setUploading(false);
    setUltimoResultado({
      uploaded: accepted,
      failed,
      failures: finalItems
        .filter((item) => item.status === "failed")
        .map((item) => ({ fileName: item.name, error: item.error || "Falha no lote" })),
    });

    await carregarResumoEvento(batch.eventId);
    await carregarLotesEvento(batch.eventId);

    if (snapshotFinal?.batch && isBatchTerminal(snapshotFinal.batch)) {
      Swal.fire({
        icon: failed > 0 ? "warning" : "success",
        title: "Lote encerrado automaticamente",
        text: `${accepted} foto(s) aceita(s) e ${failed} com falha.`,
      });
    } else if (!interruptido) {
      Swal.fire({
        icon: "info",
        title: "Envio recebido",
        text: "O lote está em processo de upload. Acompanhe o status de cada arquivo.",
      });
    }
  };

  const iniciarUpload = async () => {
    if (!eventoSelecionado?.id) {
      Swal.fire("Selecione um evento antes de enviar as fotos.");
      return;
    }

    if (activeBatch) {
      const pendingItems = queueItems.filter(
        (item) => item.status === "ready" || item.status === "not_sent" || item.status === "needs_file",
      );
      const pendingWithFile = queueItems.filter(
        (item) => item.file && (item.status === "ready" || item.status === "not_sent"),
      );

      if (!pendingWithFile.length) {
        if (pendingItems.some((item) => item.status === "needs_file")) {
          Swal.fire(
            "Reanexe os arquivos pendentes",
            "O lote foi criado, mas o envio foi bloqueado antes de receber os arquivos. Depois de cadastrar a marca-d'agua, reanexe os arquivos com os mesmos nomes e tamanhos no campo acima e clique em Retomar.",
            "info",
          );
        } else {
          Swal.fire(
            "Nenhum item seguro para retomar",
            "Atualize o snapshot para conferir o estado persistido do lote.",
            "info",
          );
        }
        return;
      }

      setBatchMonitoringPaused(false);
      persistActiveBatch({ ...activeBatch, eventId: eventoSelecionado.id }, false);
      await enviarItensDoLote(
        { ...activeBatch, eventId: eventoSelecionado.id },
        queueItems,
      );
      return;
    }

    if (!queueItems.length) {
      Swal.fire("Selecione pelo menos uma imagem.");
      return;
    }

    setUploading(true);
    setUltimoResultado(null);

    try {
      await api.get(`/upload/event-photos/${eventoSelecionado.id}/preflight`);

      const batchResp = await api.post("/upload/event-photos/batches", {
        eventId: eventoSelecionado.id,
        items: queueItems.map((item) => ({
          clientItemId: item.clientItemId,
          name: item.name,
          sizeBytes: item.sizeBytes,
        })),
      });
      const batch = batchResp.data?.batch;

      if (!batch?.id) {
        throw new Error("A API nao retornou o identificador do lote");
      }

      setBatchMonitoringPaused(false);
      setActiveBatch({ id: batch.id, eventId: eventoSelecionado.id, status: batch.status });
      persistActiveBatch({ ...batch, eventId: eventoSelecionado.id }, false);
      await carregarLotesEvento(eventoSelecionado.id);
      await enviarItensDoLote({ ...batch, eventId: eventoSelecionado.id }, queueItems);
    } catch (error) {
      setUploading(false);
      Swal.fire("Upload nao iniciado", mensagemDoErro(error), "error");
    }
  };

  const atualizarSnapshotAtivo = async () => {
    if (!activeBatch?.id) return;

    setRestoringBatch(true);
    const snapshot = await carregarSnapshotDoLote(activeBatch.id, queueItems, {
      showError: true,
    });
    setRestoringBatch(false);

    if (snapshot?.batch?.eventId) {
      await carregarLotesEvento(snapshot.batch.eventId);
      await carregarResumoEvento(snapshot.batch.eventId);
    }
  };

  const prepararNovoLoteComFalhas = () => {
    const failuresWithFile = failedItems.filter((item) => item.file);

    if (!failuresWithFile.length) {
      Swal.fire(
        "Selecione novamente os arquivos com falha",
        "Os arquivos nao ficam persistidos no navegador. Escolha-os novamente para criar um novo lote, sem reabrir o lote encerrado.",
        "info",
      );
      return;
    }

    const nextQueue = failuresWithFile.map((item) => ({
      ...item,
      clientItemId: createClientItemId(),
      status: "ready",
      remoteStatus: null,
      transportPercent: 0,
      error: null,
    }));

    setQueueItems(nextQueue);
    setProgressFromItems(nextQueue);
    setUltimoResultado(null);
  };

  const limparSelecao = () => {
    if (queueLocked) return;

    setQueueItems([]);
    setUltimoResultado(null);
    setProgress({ total: 0, processed: 0, uploaded: 0, failed: 0 });
  };

  const percentualConcluido = progress.total
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;
  const tamanhoTotalFila = queueItems.reduce(
    (total, item) => total + Number(item.sizeBytes || 0),
    0,
  );
  const formatarTamanho = (bytes) => {
    if (!bytes) return "0 MB";
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };
  const previewPhotos = resumoEvento.photos.slice(0, EVENT_PREVIEW_PHOTO_LIMIT);
  const previewRemainingCount = Math.max(
    Number(resumoEvento.totalPhotos || 0) - previewPhotos.length,
    0,
  );
  const publicationPath = eventoSelecionado?.id
    ? `/Publicacao?eventId=${encodeURIComponent(eventoSelecionado.id)}`
    : "/Publicacao";
  const previewCtaPath = canOpenPublication
    ? publicationPath
    : eventoSelecionado?.id
      ? `/Evento/Detalhe/${eventoSelecionado.id}`
      : "";
  const previewCtaTitle = canOpenPublication ? "Abrir publicação" : "Ver acervo completo";
  const previewCtaDetail = !eventoSelecionado
    ? "Escolha um evento para continuar"
    : canOpenPublication
      ? previewRemainingCount > 0
        ? `Mais ${previewRemainingCount} foto(s) na publicação`
        : "Acompanhar a fila de publicação"
      : "Abrir o detalhe permitido para este perfil";

  const podeSelecionarArquivos =
    Boolean(eventoSelecionado?.id) && (!queueLocked || canAttachMissingFiles);
  const mensagemDeOrientacao = !eventoSelecionado
    ? "Escolha um evento para liberar a seleção dos arquivos."
    : activeBatch
      ? "O lote está protegido. Itens aceitos permanecem no estado persistido; apenas pendências equivalentes podem ser retomadas."
      : queueItems.length
        ? "Confira os nomes e a quantidade. A confirmação cria um lote fechado para este evento."
        : "Selecione as imagens que serão vinculadas a este evento.";

  const getBatchStatusLabel = (status) => {
    const labels = {
      pending: "Aguardando",
      uploading: "Enviando",
      completed: "Concluido",
      partial_failed: "Concluido com falhas",
      failed: "Falhou",
    };

    return labels[status] || status;
  };

  if (!canUseUpload) {
    return (
      <div className="pagina-upload">
        <MenuLateral />
        <div className="conteudo-upload">
          <div className="upload-header-wrap">
            <CadastroPageHeader
              eyebrow="Operação de fotos"
              title="Envie as fotos do evento"
              description="Esta área fica disponível apenas para administradores e fotógrafos."
              icon={FiUploadCloud}
              context="Acesso restrito"
              contextDetail="Fale com um administrador para continuar"
            />
          </div>

          <div className="upload-bloqueio">
            <strong>Acesso restrito</strong>
            <span>
              Seu perfil nao pode enviar fotos para eventos.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pagina-upload">
      <MenuLateral />

      <div className="conteudo-upload">
        <div className="upload-header-wrap">
          <CadastroPageHeader
            eyebrow="Operação de fotos"
            title="Envie as fotos do evento"
            description="Escolha o evento, confira os arquivos e acompanhe cada lote sem perder o estado do envio."
            icon={FiUploadCloud}
            context={eventoSelecionado?.name || "Escolha um evento"}
            contextDetail={activeBatch ? "Lote ativo · retomada segura" : "Destino das próximas fotos"}
          />

          {eventoSelecionado?.id && (
            <Link className="upload-link" to={`/Evento/Detalhe/${eventoSelecionado.id}`}>
              <FiArrowRight aria-hidden="true" />
              Ver fotos do evento
            </Link>
          )}
        </div>

        <div className="upload-layout">
          <section className="upload-eventos" aria-labelledby="upload-eventos-title">
            <div className="upload-card-header">
              <div className="upload-heading-content">
                <span className="upload-panel-kicker">Destino do lote</span>
                <h2 id="upload-eventos-title">Escolha um evento</h2>
                <p>As fotos ficam vinculadas ao evento selecionado.</p>
              </div>

              <span className="upload-count-badge">
                {eventosFiltrados.length} {eventosFiltrados.length === 1 ? "evento" : "eventos"}
              </span>
            </div>

            <FieldLabel label="Buscar evento" className="upload-search-field">
              <span className="upload-search-box">
                <FiSearch aria-hidden="true" />
                <input
                  className="upload-search"
                  placeholder="Buscar evento, local, turma..."
                  value={buscaEvento}
                  disabled={queueLocked}
                  onChange={(event) => setBuscaEvento(event.target.value)}
                />
              </span>
            </FieldLabel>

            {loadingEventos && (
              <div className="upload-empty" role="status">Carregando eventos...</div>
            )}

            {!loadingEventos && eventosFiltrados.length === 0 && (
              <div className="upload-empty" role="status">
                {role === USER_ROLES.PHOTOGRAPHER
                  ? "Nenhum evento vinculado ao seu perfil."
                  : "Nenhum evento encontrado."}
              </div>
            )}

            <div className="upload-event-list">
              {eventosFiltrados.map((evento) => {
                const ativo = eventoSelecionado?.id === evento.id;

                return (
                  <button
                    key={evento.id}
                    type="button"
                    disabled={queueLocked}
                    className={`upload-event-item ${ativo ? "ativo" : ""}`}
                    aria-pressed={ativo}
                    onClick={() => setEventoSelecionado(evento)}
                  >
                    <strong>{evento.name}</strong>
                    <span className="upload-event-meta">
                      <span><FiCalendar aria-hidden="true" /> {new Date(evento.eventDate).toLocaleDateString()}</span>
                      <span><FiMapPin aria-hidden="true" /> {evento.location || "Local nao informado"}</span>
                    </span>
                    <span style={{ overflowWrap: "anywhere" }}>{formatarTurmas(evento.classes || [])}</span>
                    <small>{formatarInstituicoes(evento.classes || [])}</small>
                    {ativo && <span className="upload-event-selected"><FiCheckCircle aria-hidden="true" /> Selecionado</span>}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="upload-painel" aria-labelledby="upload-panel-title">
            <div className="upload-card-header">
              <div className="upload-heading-content">
                <span className="upload-panel-kicker">Entrada de imagens</span>
                <h2 id="upload-panel-title">Prepare o lote</h2>
                <p>Faça a conferência antes de iniciar o envio.</p>
              </div>
              <span className="upload-count-badge upload-capacity-badge">
                {queueItems.length}/{uploadCapacity.maxFilesPerManifest} arquivos
              </span>
            </div>

            {(loadingCapacity || capacityError) && (
              <div className={`upload-stream-status ${capacityError ? "is-warning" : ""}`} role="status">
                {loadingCapacity ? "Carregando limites do servidor..." : capacityError}
              </div>
            )}

            <div className="upload-resumo-evento">
              <span className="upload-context-icon"><FiCalendar aria-hidden="true" /></span>
              <div>
                <span className="upload-label">Evento selecionado</span>
                <strong>{eventoSelecionado?.name || "Nenhum evento selecionado"}</strong>
                <small>
                  {eventoSelecionado
                    ? `${new Date(eventoSelecionado.eventDate).toLocaleDateString()} • ${formatarInstituicoes(eventoSelecionado.classes || [])}`
                    : "Escolha um evento na lista ao lado."}
                </small>
              </div>
              <span className="upload-context-status">
                {activeBatch ? "Lote ativo" : eventoSelecionado ? "Pronto para receber" : "Aguardando evento"}
              </span>
            </div>

            <div className="upload-guidance" role="status">
              <FiClock aria-hidden="true" />
              <span><strong>Próximo passo</strong>{mensagemDeOrientacao}</span>
            </div>

            <label
              className={`upload-dropzone ${!podeSelecionarArquivos ? "disabled" : ""}`}
            >
              <input
                type="file"
                aria-label="Selecionar imagens do lote"
                accept={acceptedFileTypes}
                multiple
                disabled={!podeSelecionarArquivos}
                onChange={selecionarArquivos}
              />
              <span className="upload-dropzone-icon"><FiUploadCloud aria-hidden="true" /></span>
              <strong>
                {canAttachMissingFiles
                  ? "Reanexar arquivos pendentes"
                  : eventoSelecionado ? "Selecionar imagens" : "Escolha um evento primeiro"}
              </strong>
              <span>
                {activeBatch
                  ? "O lote esta fechado: somente arquivos pendentes equivalentes podem ser reanexados."
                  : eventoSelecionado
                    ? `${queueItems.length} arquivo(s) pronto(s) para o lote`
                    : "O evento define o destino e libera o campo de arquivos."}
              </span>
              <small>
                {allowedMimeTypes.map((type) => MIME_LABELS[type] || type).join(", ")} · até {maxFileSizeMb} MB por arquivo
              </small>
            </label>

            {queueItems.length > 0 && (
              <div className="upload-queue-section">
                <div className="upload-card-header compact">
                  <div className="upload-heading-content">
                    <span className="upload-panel-kicker">Conferência</span>
                    <h3>Fila deste lote</h3>
                  </div>
                  <span className="upload-count-badge">
                    {queueItems.length} selecionado(s) · {formatarTamanho(tamanhoTotalFila)}
                  </span>
                </div>

                <div className="upload-queue-toolbar">
                  <form
                    className="upload-queue-search"
                    role="search"
                    aria-label="Buscar arquivos na fila"
                    onSubmit={(event) => {
                      event.preventDefault();
                      setFileSearch(fileSearchInput);
                    }}
                  >
                    <label htmlFor="upload-filename-search">Nome do arquivo</label>
                    <div className="upload-queue-search-controls">
                      <span className="upload-queue-search-field">
                        <FiSearch aria-hidden="true" />
                        <input
                          id="upload-filename-search"
                          type="search"
                          placeholder="Ex.: turma-2026.jpg"
                          value={fileSearchInput}
                          onChange={(event) => setFileSearchInput(event.target.value)}
                          aria-describedby="upload-queue-search-help"
                        />
                      </span>
                      <button className="upload-queue-search-submit" type="submit">
                        Buscar
                      </button>
                      <button
                        className="upload-queue-search-clear"
                        type="button"
                        onClick={() => {
                          setFileSearchInput("");
                          setFileSearch("");
                        }}
                        disabled={!fileSearchInput && !fileSearch}
                      >
                        Limpar busca
                      </button>
                    </div>
                  </form>
                  <div className="upload-queue-search-feedback">
                    <span id="upload-queue-summary" role="status">
                      Exibindo {visibleQueueItems.length} de {queueItems.length} arquivo(s).
                    </span>
                    <small id="upload-queue-search-help">
                      A busca altera apenas a lista exibida. Todos os arquivos continuam no envio.
                    </small>
                  </div>
                </div>

                <div className="upload-queue-scroll" ref={queueScrollRef}
                  role="region" aria-label="Arquivos do lote" tabIndex={0}>
                  {visibleQueueItems.length === 0 ? (
                    <p className="upload-queue-empty">Nenhum arquivo corresponde à busca. O lote mantém seus {queueItems.length} arquivo(s).</p>
                  ) : (
                    <div className="upload-files" role="list" aria-label="Fila do lote">
                      {visibleQueueItems.map((item) => (
                        <div key={item.clientItemId} className={`upload-file-chip ${item.status}`} role="listitem">
                          <span className="upload-file-icon"><FiImage aria-hidden="true" /></span>
                          <div className="upload-file-body">
                            <div className="upload-file-heading">
                              <strong>{item.name}</strong>
                              {!queueLocked && (
                                <button
                                  type="button"
                                  className="upload-file-remove"
                                  aria-label={`Remover ${item.name}`}
                                  onClick={() => removerArquivoDaFila(item.clientItemId)}
                                >
                                  Remover
                                </button>
                          )}
                        </div>
                        <span>{(item.sizeBytes / 1024 / 1024).toFixed(2)} MB</span>
                        <small className={`upload-item-status ${item.status}`}>
                          {ITEM_STATUS_LABELS[item.status] || item.status}
                          {item.status === "sending" && item.transportPercent > 0
                            ? ` (${item.transportPercent}%)`
                            : ""}
                        </small>
                        {item.status === "sending" && (
                          <div
                            className="upload-file-progress"
                            role="progressbar"
                            aria-label={`Progresso de ${item.name}`}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow={item.transportPercent || 0}
                          >
                            <span style={{ width: `${item.transportPercent || 0}%` }} />
                          </div>
                        )}
                        {item.error && <small className="upload-item-error">{item.error}</small>}
                      </div>
                    </div>
                  ))}
                </div>
                  )}
                </div>
              </div>
            )}

            <div className="upload-actions">
              <button
                type="button"
                className="upload-primary"
                onClick={iniciarUpload}
                disabled={uploading || restoringBatch || (!activeBatch && queueItems.length === 0)}
              >
                {uploading ? <FiRefreshCw className="upload-button-icon upload-spin" aria-hidden="true" /> : <FiUploadCloud className="upload-button-icon" aria-hidden="true" />}
                {uploading
                  ? "Enviando..."
                  : activeBatch
                    ? "Retomar itens seguros"
                    : "Confirmar lote e iniciar upload"}
              </button>

              <button
                type="button"
                className="upload-secondary"
                disabled={queueLocked}
                onClick={limparSelecao}
              >
                Limpar selecao
              </button>

              {activeBatch && (
                <button
                  type="button"
                  className="upload-secondary"
                  disabled={uploading || restoringBatch}
                  onClick={atualizarSnapshotAtivo}
                >
                  <FiRefreshCw className="upload-button-icon" aria-hidden="true" />
                  {restoringBatch ? "Atualizando..." : "Atualizar estado do lote"}
                </button>
              )}

              {!activeBatch && failedItems.length > 0 && (
                <button
                  type="button"
                  className="upload-secondary"
                  onClick={prepararNovoLoteComFalhas}
                >
                  <FiUploadCloud className="upload-button-icon" aria-hidden="true" />
                  Criar novo lote com falhas
                </button>
              )}
            </div>

            {activeBatch && streamStatus && (
              <div className="upload-stream-status upload-live-status" role="status">
                <FiRefreshCw aria-hidden="true" />
                <span>{streamStatus}</span>
              </div>
            )}

            <div className="upload-progress-card" aria-labelledby="upload-progress-title">
              <div className="upload-progress-heading">
                <div className="upload-heading-content">
                  <span className="upload-panel-kicker">Acompanhamento</span>
                  <h3 id="upload-progress-title">Estado deste lote</h3>
                </div>
                <strong className="upload-progress-percent">{percentualConcluido}%</strong>
              </div>

              <div className="upload-progress-stats">
                <div><span>Total</span><strong>{progress.total}</strong></div>
                <div><span>Terminais no lote</span><strong>{progress.processed}</strong></div>
                <div><span>Aceitas</span><strong>{progress.uploaded}</strong></div>
                <div><span>Falhas</span><strong>{progress.failed}</strong></div>
              </div>

              <div
                className="upload-progress-bar"
                role="progressbar"
                aria-label="Itens em estado terminal"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={percentualConcluido}
              >
                <div className="upload-progress-fill" style={{ width: `${percentualConcluido}%` }} />
              </div>

              <small>{percentualConcluido}% em estado terminal. Upload concluído não significa publicação automática.</small>
            </div>

            <div className="upload-support-grid">
              <div className="upload-preview-card">
                <div className="upload-card-header compact">
                  <div className="upload-heading-content">
                    <span className="upload-panel-kicker">Acervo do evento</span>
                    <h3>Aperitivo do acervo</h3>
                    <span>
                      {resumoEvento.loading
                        ? "Atualizando..."
                        : `${previewPhotos.length} de ${resumoEvento.totalPhotos} foto(s) exibidas`}
                    </span>
                  </div>
                </div>

                <div className="upload-preview-grid" aria-label="Aperitivo do acervo do evento">
                  {previewPhotos.map((photo) => (
                    <AuthenticatedMedia
                      key={photo.photoId}
                      path={photo.media?.thumbnail?.path}
                      alt={photo.originalName}
                      className="upload-preview-image"
                    />
                  ))}

                  {previewCtaPath ? (
                    <Link
                      className="upload-preview-cta"
                      to={previewCtaPath}
                      aria-label={`${previewCtaTitle}${eventoSelecionado ? ` de ${eventoSelecionado.name}` : ""}`}
                    >
                      <span className="upload-preview-cta-icon"><FiArrowRight aria-hidden="true" /></span>
                      <strong>{previewCtaTitle}</strong>
                      <small>{previewCtaDetail}</small>
                    </Link>
                  ) : (
                    <div className="upload-preview-cta is-disabled" aria-disabled="true">
                      <span className="upload-preview-cta-icon"><FiArrowRight aria-hidden="true" /></span>
                      <strong>{previewCtaTitle}</strong>
                      <small>{previewCtaDetail}</small>
                    </div>
                  )}
                </div>

                {!resumoEvento.loading && previewPhotos.length === 0 && (
                  <p className="upload-preview-empty-copy">Nenhuma foto enviada para este evento ainda.</p>
                )}
              </div>

              <div className="upload-batches-card">
                <div className="upload-card-header compact">
                  <div className="upload-heading-content">
                    <span className="upload-panel-kicker">Histórico</span>
                    <h3>Lotes recentes</h3>
                    <span>
                      {loadingBatches
                        ? "Atualizando..."
                        : `${uploadBatches.length} lote(s) listado(s)`}
                    </span>
                  </div>
                </div>

                <div className="upload-batch-list">
                  {uploadBatches.map((batch) => (
                    <div key={batch.id} className={`upload-batch-item ${batch.status}`}>
                      <div className="upload-batch-main">
                        <strong>{getBatchStatusLabel(batch.status)}</strong>
                        <span>
                          {batch.processedFiles}/{batch.totalFiles} processado(s) · {batch.uploadedFiles} enviado(s) · {batch.failedFiles} erro(s)
                        </span>
                      </div>
                      <div className="upload-batch-progress">
                        <div style={{ width: `${batch.progressPercent || 0}%` }} />
                      </div>
                      <small>
                        {batch.message || "Sem mensagem"} · {batch.createdAt ? new Date(batch.createdAt).toLocaleString() : "-"}
                      </small>
                    </div>
                  ))}

                  {!loadingBatches && uploadBatches.length === 0 && (
                    <div className="upload-empty small">
                      Nenhum lote registrado para este evento.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {ultimoResultado && ultimoResultado.failed > 0 && (
              <div className="upload-errors">
                <strong>Falhas do ultimo lote</strong>

                {ultimoResultado.failures.slice(0, 8).map((item, index) => (
                  <div
                    key={`${item.fileName}-${index}`}
                    className="upload-error-item"
                  >
                    <span>{item.fileName}</span>
                    <small>{item.error}</small>
                  </div>
                ))}

                {ultimoResultado.failures.length > 8 && (
                  <small className="upload-error-more">
                    +{ultimoResultado.failures.length - 8} falha(s) adicional(is)
                  </small>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default Upload;
